import { useEffect } from 'react';
import invert from 'lodash/invert';
import isNil from 'lodash/isNil';
import {
	StatusCategoryTypes,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { getStatusShape } from '@atlassian/jira-issue-field-status/src/common/utils.tsx';
import { StatusKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check/index.tsx';
import type { Card } from '../../../../model/types.tsx';
import { cardStatusSet } from '../../../../state/actions/status/index.tsx';
import { useBacklogSelector, useBacklogActionCreator } from '../../../../state/index.tsx';
import { getIsCompanyManaged } from '../../../../state/selectors/app/index.tsx';
import { getColumns } from '../../../../state/selectors/columns/index.tsx';
import { getColumnStatusByStatusId } from '../../../../state/selectors/statuses/index.tsx';

export type Props = {
	card: Card;
};

const FieldSyncStatus = ({ card }: Props) => {
	const columns = useBacklogSelector(getColumns);
	const statusFromBacklog = useBacklogSelector((state) =>
		card ? getColumnStatusByStatusId(card.statusId)(state) : null,
	);
	const isCMP = useBacklogSelector(getIsCompanyManaged);

	const { key: cardKey, id: cardId, statusId: cardStatusId } = card;

	const setStatusInBacklog = useBacklogActionCreator(cardStatusSet);
	const {
		id: statusIdFromBacklog,
		name: statusNameFromBacklog,
		category: statusCategoryFromBacklog,
	} = statusFromBacklog || {};

	const [statusFromExtractedFields, { setFieldValue: setStatusInExtractedFields }] = useFieldValue({
		issueKey: cardKey,
		fieldKey: StatusKey,
	});

	const { id: statusIdFromExtractedFields, statusCategory: statusCategoryFromExtractedFields } =
		statusFromExtractedFields || {};

	const statusFromBacklogChanged = useDiffCheck([statusIdFromBacklog]);
	const statusFromExtractedFieldsChanged = useDiffCheck([statusIdFromExtractedFields]);

	const storesNeedToSync = statusIdFromBacklog !== statusIdFromExtractedFields;

	useEffect(() => {
		const statusFromBacklogIsDefined = !isNil(statusFromBacklog) && Boolean(statusIdFromBacklog);

		// STATUS SYNC: BACKLOG -> EXTRACTED FIELDS
		// initial value, realtime...
		if (statusFromBacklogIsDefined && statusFromBacklogChanged && storesNeedToSync) {
			setStatusInExtractedFields(
				cardKey,
				StatusKey,
				getStatusShape(
					// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
					statusIdFromBacklog,
					statusNameFromBacklog,
					statusCategoryFromBacklog,
				),
			);
		}
	}, [
		cardKey,
		statusFromBacklog,
		statusIdFromBacklog,
		statusNameFromBacklog,
		statusCategoryFromBacklog,
		statusFromBacklogChanged,
		storesNeedToSync,
		setStatusInExtractedFields,
	]);

	useEffect(() => {
		const statusFromExtractedFieldsIsDefined =
			!isNil(statusFromExtractedFields) && Boolean(statusIdFromExtractedFields);

		// STATUS SYNC: EXTRACTED FIELDS -> BACKLOG REDUX
		// issue view updates, inline status edit...
		if (
			// Ensure no sync will be called by Backlog as Backlog is the driver
			(!fg('jsw_backlog_missing_selected_issue_fix') || columns !== null) &&
			!statusFromBacklogChanged &&
			statusFromExtractedFieldsIsDefined &&
			statusFromExtractedFieldsChanged &&
			storesNeedToSync
		) {
			setStatusInBacklog(
				cardId,
				cardStatusId,
				statusIdFromExtractedFields,
				// @ts-expect-error TS2345: Argument of type 'string' is not assignable to parameter of type 'StatusCategoryKeys'.
				invert(StatusCategoryIds)[statusCategoryFromExtractedFields.id] ||
					StatusCategoryTypes.UNDEFINED,
				isCMP,
			);
		}
	}, [
		columns,
		cardId,
		cardStatusId,
		statusFromBacklogChanged,
		statusFromExtractedFields,
		statusIdFromExtractedFields,
		statusCategoryFromExtractedFields,
		statusFromExtractedFieldsChanged,
		storesNeedToSync,
		setStatusInBacklog,
		isCMP,
	]);

	return null;
};

export default FieldSyncStatus;
